// @flow
/* eslint-disable no-nested-ternary */

import * as React from 'react';
import styled, {
  type StyledComponent,
  type PropsWithTheme,
} from 'styled-components';

import { fontWeight, fontSize, fontSmooth } from 'ui/styles';
import type { Theme } from 'ui/styles';

export type BaseProps = $ReadOnly<{|
  children?: React.Node,
  content?: boolean,
  icon?: React.ComponentType<any>,
  leftIcon?: boolean,
  iconSize?: number,
  iconRotation?: string,
  active?: boolean,
  className?: ?string,
  danger?: boolean,
  disabled?: boolean,
  primary?: boolean,
  secondary?: boolean,
  small?: boolean,
  large?: boolean,
  style?: { [string]: string | number, ... },
  tertiary?: boolean,
  onClick?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  onMouseEnter?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  onMouseLeave?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  onTouchEnd?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  onTouchStart?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  tabIndex?: string | number,
  value?: mixed,
  weight?: 'normal' | 'semibold',
  type?: string,
  flatBottom?: boolean,
  flatTop?: boolean,
  'aria-pressed'?: boolean,
  innerRef?: React.Ref<any>,
  'data-testid'?: string,
  innerBorderRadius?: string,
|}>;

export type Props = $ReadOnly<{|
  ...BaseProps,
|}>;

type ButtonPropsWithTheme = $ReadOnly<{|
  ...Props,
  theme: Theme,
  focus?: boolean,
  hover?: boolean,
  hasChildren?: boolean,
|}>;

const type = ({
  danger,
  disabled,
  primary,
  secondary,
  tertiary,
}: {|
  danger: void | boolean,
  disabled: void | boolean,
  primary: void | boolean,
  secondary: void | boolean,
  tertiary: void | boolean,
|}): string => {
  if (disabled) {
    return 'disabled';
  }
  if (primary) {
    return 'primary';
  }
  if (secondary) {
    return 'secondary';
  }
  if (tertiary) {
    return 'tertiary';
  }
  if (danger) {
    return 'danger';
  }
  return 'tertiary';
};

const backgroundColor = ({
  active,
  danger,
  disabled,
  focus,
  hover,
  primary,
  secondary,
  tertiary,
  theme,
}: ButtonPropsWithTheme): string => {
  const {
    black,
    gray_1: gray1,
    gray_2: gray2,
    gray_3: gray3,
    gray_4: gray4,
    gray_5: gray5,
    red_active: redActive,
    red_hover: redHover,
    red,
    white,
  } = theme;
  switch (
    type({
      danger,
      disabled,
      primary,
      secondary,
      tertiary,
    })
  ) {
    case 'primary':
      return active ? black : hover ? gray5 : black;
    case 'secondary':
      return active ? gray4 : hover ? gray3 : focus ? gray1 : gray2;
    case 'tertiary':
      return active ? gray2 : hover ? gray1 : white;
    case 'danger':
      return active ? redActive : hover ? redHover : red;
    case 'disabled':
      return gray1;
    default:
      return active ? gray3 : gray2;
  }
};

const borderColor = ({
  active,
  danger,
  disabled,
  focus,
  hover,
  primary,
  secondary,
  tertiary,
  theme,
}: ButtonPropsWithTheme): string => {
  const {
    black,
    gray_1: gray1,
    gray_2: gray2,
    gray_3: gray3,
    gray_4: gray4,
    gray_5: gray5,
    red_active: redActive,
    red_hover: redHover,
    red,
  } = theme;
  switch (
    type({
      primary,
      secondary,
      tertiary,
      danger,
      disabled,
    })
  ) {
    case 'disabled':
      return gray1;
    case 'primary':
      return active ? black : hover ? gray5 : black;
    case 'secondary':
      return active ? gray4 : hover ? gray3 : focus ? gray1 : gray2;
    case 'danger':
      return active ? redActive : hover ? redHover : red;
    case 'tertiary':
    default:
      return black;
  }
};

const borderRadius = (
  small: void | boolean,
  large: void | boolean,
  flatTop: void | boolean,
  flatBottom: void | boolean,
): string => {
  const base = small ? '13px' : large ? '24.5px' : '17px';
  if (flatTop) return `0 0 ${base} ${base}`;
  if (flatBottom) return `${base} ${base} 0 0`;

  return base;
};

const color = ({
  danger,
  disabled,
  primary,
  secondary,
  tertiary,
  theme,
}: ButtonPropsWithTheme): string => {
  const { black, gray_3: gray3, white } = theme;
  switch (
    type({
      primary,
      secondary,
      tertiary,
      danger,
      disabled,
    })
  ) {
    case 'disabled':
      return gray3;
    case 'secondary':
    case 'tertiary':
      return black;
    case 'primary':
      return white;
    case 'danger':
    default:
      return white;
  }
};

const focusBorder = ({
  active,
  danger,
  disabled,
  focus,
  hover,
  primary,
  secondary,
  tertiary,
  theme,
}: ButtonPropsWithTheme): string => {
  const {
    black,
    gray_1: gray1,
    gray_2: gray2,
    gray_3: gray3,
    gray_4: gray4,
    gray_5: gray5,
    red_active: redActive,
    red_hover: redHover,
    red,
    white,
  } = theme;
  switch (
    type({
      danger,
      disabled,
      primary,
      secondary,
      tertiary,
    })
  ) {
    case 'primary':
      return active ? black : hover ? gray5 : white;
    case 'secondary':
      return active ? gray4 : hover ? gray3 : focus ? gray3 : gray2;
    case 'tertiary':
      return active ? gray2 : hover ? gray1 : focus ? gray3 : white;
    case 'danger':
      return active ? redActive : hover ? redHover : focus ? white : red;
    case 'disabled':
      return gray1;
    default:
      return black;
  }
};

const padding = ({
  hasChildren,
  icon,
  leftIcon,
  small,
  large,
}: {|
  +active?: boolean,
  +'aria-pressed'?: boolean,
  +children?: React.Node,
  +className?: ?string,
  +content?: boolean,
  +danger?: boolean,
  +'data-testid'?: string,
  +disabled?: boolean,
  +flatBottom?: boolean,
  +flatTop?: boolean,
  +hasChildren?: boolean,
  +icon?: React.ComponentType<any>,
  +iconRotation?: string,
  +iconSize?: number,
  +innerBorderRadius?: string,
  +innerRef?: React.Ref<any>,
  +large?: boolean,
  +leftIcon?: boolean,
  +onClick?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  +onMouseEnter?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  +onMouseLeave?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  +onTouchEnd?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  +onTouchStart?: (SyntheticMouseEvent<HTMLButtonElement>) => void,
  +primary?: boolean,
  +secondary?: boolean,
  +small?: boolean,
  +style?: { [string]: string | number },
  +tabIndex?: string | number,
  +tertiary?: boolean,
  +theme: Theme,
  +type?: string,
  +value?: mixed,
  +weight?: 'normal' | 'semibold',
|}): string => {
  if (small) {
    return '5px 8px';
  }

  if (large) {
    return '12.5px 32px';
  }

  if (hasChildren) {
    if (!icon) {
      return '6px 16px';
    }

    return leftIcon ? '6px 16px 6px 8px' : '6px 8px 6px 16px';
  }

  return '6px 8px';
};

const innerBorder = ({
  active,
  hover,
  small,
  large,
  innerBorderRadius,
  ...props
}: ButtonPropsWithTheme) => `
&:after {
  content: " ";
  position: absolute;
  height: calc(100% - ${small ? '4px' : large ? '8px' : '6px'});
  width: calc(100% - ${small ? '4px' : large ? '8px' : '6px'});
  top: ${small ? '1px' : large ? '3px' : '2px'};
  left: ${small ? '1px' : large ? '3px' : '2px'};
  border: 1px solid ${focusBorder({ ...props, active, hover })};
  pointer-events: none;
  border-radius: ${
    innerBorderRadius || (small ? '11px' : large ? '24.5px' : '15px')
  }
};`;
const iconMarginLeft = ({
  hasChildren,
  small,
  large,
}: {|
  children?: React$Node,
  className?: ?string,
  hasChildren?: boolean,
  icon: mixed,
  large?: boolean,
  leftIcon?: boolean,
  rotation?: ?string,
  size?: number,
  small?: boolean,
  style?: { [string]: string | number } | { +[string]: mixed },
  theme: Theme,
|}) => {
  if (!hasChildren) {
    return '0px';
  }
  return small ? '4px' : large ? '16px' : '5px';
};

export const baseStyles = (
  props: $ReadOnly<{|
    ...Props,
    hasChildren?: boolean,
    theme: Theme,
    innerBorderRadius?: string,
  |}>,
): string => `
    &:focus {
      ${innerBorder({
        ...props,
        active: false,
        focus: true,
        hover: false,
      })}
      outline: none;
      color: ${color({ ...props, active: false, focus: true })};
    }
    &:hover,
    &:active {
      cursor: pointer;
    }
    &:hover {
      ${innerBorder({ ...props, hover: true })}
      background-color: ${backgroundColor({
        ...props,
        focus: false,
        hover: true,
      })};
      border-color: ${borderColor({ ...props, focus: false, hover: true })};
      color: ${color({ ...props })};
    }
    &:active {
      ${innerBorder({ ...props, active: true, hover: true })}
      background-color: ${backgroundColor({
        ...props,
        active: true,
        focus: false,
        hover: true,
      })};
      border-color: ${borderColor({
        ...props,
        active: true,
        focus: false,
        hover: false,
      })};
      color: ${color({ ...props, active: true, focus: false })};
    }
    &:disabled {

      .not-allowed-wrapper {
        display: block;
      }

      background-color: ${backgroundColor({
        ...props,
        active: false,
        disabled: true,
        focus: false,
        hover: false,
      })};
      color: ${color({ ...props })};
      /* https://github.com/facebook/react/issues/4251 */
      pointer-events: none;
    }

    .not-allowed-wrapper {
      display: none;
      cursor: not-allowed;
      position: absolute;
      height: 100%;
      width: 100%;
      pointer-events: all;
    }

    align-items: center;
    background-color: ${backgroundColor({
      ...props,
      focus: false,
      hover: false,
    })};
    border-radius: ${borderRadius(
      props.small,
      props.large,
      props.flatTop,
      props.flatBottom,
    )};
    border-width: 1px;
    border: 1px solid ${borderColor({ ...props, focus: false, hover: false })};
    color: ${color({ ...props, active: false, focus: false })};
    display: inline-flex;
    flex-direction: row;
    ${fontSize({ fSize: props.small ? 2 : props.large ? 4 : 3 })};
    ${fontSmooth()};
    justify-content: center;
    padding: ${padding(props)};
    position: relative;
    ${fontWeight({ weight: props.weight || 'normal' })};
  `;
const BaseButton: StyledComponent<
  { ...Props, hasChildren?: boolean },
  Theme,
  HTMLButtonElement,
> = styled.button`
  ${(props: PropsWithTheme<{ ...Props, hasChildren?: boolean, ... }, Theme>) =>
    baseStyles(props)}
`;

export const LinkButton: StyledComponent<
  {},
  Theme,
  HTMLButtonElement,
> = styled.button`
  color: ${({ theme }: PropsWithTheme<{ ... }, Theme>) => theme.blue};
  background-color: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  display: inline;
  margin: 0;
  padding: 0;
`;

export const Wrapper = BaseButton;

type IconStyleProps = $ReadOnly<{|
  size?: number,
  small?: boolean,
  large?: boolean,
  style?: { +[string]: mixed },
  hasChildren?: boolean,
  leftIcon?: boolean,
  icon: mixed,
  rotation?: ?string,
|}>;

export const ButtonIconNode: StyledComponent<
  IconStyleProps,
  Theme,
  HTMLDivElement,
> = styled.div`
  margin-left: ${(props: PropsWithTheme<IconStyleProps, Theme>) =>
    props.leftIcon ? 'unset' : iconMarginLeft(props)};
  margin-right: ${(props: PropsWithTheme<IconStyleProps, Theme>) =>
    props.leftIcon ? iconMarginLeft(props) : 'unset'};
  align-items: center;
  order: ${(props: PropsWithTheme<IconStyleProps, Theme>) =>
    String(props.leftIcon ? 0 : 1)};
  display: inline-flex;
  ${({ rotation }: PropsWithTheme<IconStyleProps, Theme>) =>
    rotation ? `transform: rotate(${rotation});` : ''};
`;

// This will be inline-block in @elastic-internal/lucide icons otherwise
const iconStyle = {
  display: 'inline-flex',
};

export const receivedChildren = (children: any): boolean =>
  typeof children !== 'undefined' &&
  children !== null &&
  (typeof children === 'number' ||
    typeof children === 'string' ||
    children === true ||
    (Array.isArray(children) && !!children[0]) ||
    typeof children === 'object');

const Button = (
  { innerRef, children, icon: IconComp, ...props }: Props,
  forwardedRef: React.Ref<'button'>,
) => {
  const hasChildren = receivedChildren(children);

  return (
    /* $FlowFixMe[incompatible-use] */
    <BaseButton {...{ ...props, hasChildren }} ref={forwardedRef || innerRef}>
      <div className="not-allowed-wrapper" />
      {IconComp && props.leftIcon && (
        /* $FlowFixMe[incompatible-use] */
        <ButtonIconNode
          hasChildren={hasChildren}
          small={props.small}
          large={props.large}
          rotation={props.iconRotation}
          style={iconStyle}
          leftIcon
        >
          <IconComp size={props.iconSize || 16} />
        </ButtonIconNode>
      )}
      {children}
      {IconComp && !props.leftIcon && (
        /* $FlowFixMe[incompatible-use] */
        <ButtonIconNode
          hasChildren={hasChildren}
          small={props.small}
          large={props.large}
          rotation={props.iconRotation}
          style={iconStyle}
        >
          <IconComp size={props.iconSize || 16} />
        </ButtonIconNode>
      )}
    </BaseButton>
  );
};
export default (React.forwardRef<Props, HTMLButtonElement>(
  Button,
): React$AbstractComponent<Props, HTMLButtonElement>);
